import React, { useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../../contexts/AuthContext';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import studyService from '../../services/studyService';
import permissionsService from '../../services/permissionsService';
import { 
  Book, 
  Plus, 
  Loader2, 
  RefreshCw,
  ChevronDown,
  Search, 
  ChevronRight, 
  Grid, 
  List, 
  Beaker, 
  SortAsc, 
  SortDesc, 
  Filter,
  Calendar,
  ArrowUpRight,
  ChevronLeft,
  CheckCircle,
  Clock,
  AlertCircle,
  MoveRight,
  ArrowRight
} from 'lucide-react';
import SponsorSelector from '../Sponsor/SponsorSelector';
import PageHeader from '../Shared/PageHeader';
import EmptyState from '../ui/EmptyState';

const convertToCSV = (studies) => {
  if (!Array.isArray(studies) || studies.length === 0) return '';

  const headers = ['ID', 'Title', 'Status', 'Last Updated'];
  const rows = studies.map(study => [
    study.id,
    study.title,
    study.status.replace('_', ' '),
    study.updated_at ? new Date(study.updated_at).toLocaleDateString() : 'N/A'
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  return csvContent;
};

const StudiesPage = () => {
  const { isAuthenticated, user } = useContext(AuthContext);
  const { isDemo } = useEnvironment();
  const navigate = useNavigate();
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('studyViewPreferences.viewMode') || 'grid';
  });
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('studyViewPreferences.sortBy') || 'lastUpdated';
  });
  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('studyViewPreferences.sortOrder') || 'desc';
  });
  const [filterStatus, setFilterStatus] = useState(() => {
    return localStorage.getItem('studyViewPreferences.filterStatus') || 'all';
  });
  const [showSponsorSelector, setShowSponsorSelector] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [permissions, setPermissions] = useState({
    canAccessStudyBuilder: false
  });

  // Strict type checking for is_internal - explicitly check for false
  const isInternalUser = user?.is_internal === true;
  
  // Optional: Clear preferences on logout
  const clearPreferences = useCallback(() => {
    localStorage.removeItem('studyViewPreferences.viewMode');
    localStorage.removeItem('studyViewPreferences.sortBy');
    localStorage.removeItem('studyViewPreferences.sortOrder');
    localStorage.removeItem('studyViewPreferences.filterStatus');
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      clearPreferences();
    }
  }, [isAuthenticated, clearPreferences]);

  // Prevent non-internal users from accessing study creation
  useEffect(() => {
    if (!isInternalUser) {
      const path = window.location.pathname;
      if (path.includes('/studybuilder')) {
        navigate('/studies');
      }
    }
  }, [user, navigate, isInternalUser]);

  const fetchStudies = useCallback(async () => {
    // Backend extracts sponsor_id from JWT token automatically via get_sponsor_id_from_request()
    // No need to wait for selectedSponsor in localStorage
    
    if (isAuthenticated) {
      try {
        const response = await studyService.getStudies();
        
        // Handle both array and object responses
        // studyService now returns the results array directly
        const studiesData = Array.isArray(response) ? response : [];
        
        const filteredStudies = studiesData.filter(study => study && study.id);

        // For users with only one study, redirect directly to that study (bypass study selection)
        // Exception: Program Admins and superusers should always see the study list
        const shouldBypassStudyList = filteredStudies.length === 1 
          && user?.role !== 'Program Admin' 
          && !user?.is_superuser;
        
        if (shouldBypassStudyList) {
          console.log(`[STUDY_BYPASS] User has exactly 1 study, auto-redirecting to: ${filteredStudies[0].title} (ID: ${filteredStudies[0].id})`);
          navigate(`/studies/${filteredStudies[0].external_id}`);
          return;
        }
        
        setStudies(filteredStudies);
        setError(null);
      } catch (error) {
        console.error('Error fetching studies:', error);
        setError(error.message || 'Failed to fetch studies');
      } finally {
        setLoading(false);
      }
    }
  }, [isAuthenticated, isInternalUser, navigate, user]);

  const initialized = useRef(false);
  
  // Track sponsor context to re-fetch when it changes
  const [sponsorContext, setSponsorContext] = useState(() => {
    return localStorage.getItem('selectedSponsor');
  });

  // Derive sponsor name from the stored JSON
  const sponsorName = useMemo(() => {
    try {
      const parsed = JSON.parse(sponsorContext);
      return parsed?.name || null;
    } catch {
      return null;
    }
  }, [sponsorContext]);
  
  // Listen for localStorage changes (e.g., sponsor selection in another tab or after auto-bypass)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'selectedSponsor') {
        console.log('[STUDIES] Sponsor context changed via storage event');
        setSponsorContext(e.newValue);
        // Reset initialization to allow re-fetch with new sponsor context
        initialized.current = false;
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    // Update sponsor context state when component receives focus (handles same-tab updates)
    const currentSponsor = localStorage.getItem('selectedSponsor');
    if (currentSponsor !== sponsorContext) {
      console.log('[STUDIES] Sponsor context updated on effect run');
      setSponsorContext(currentSponsor);
      initialized.current = false; // Reset to allow fetch
    }
    
    if (initialized.current)
      return;
    initialized.current = true;

    const initializeStudies = async () => {
      try {
        await fetchStudies();
      } catch (error) {
        console.error('Error initializing studies:', error);
        setError(error.message || 'Failed to initialize studies');
      } finally {
        setLoading(false);
      }
    };

    initializeStudies();
  }, [user, navigate, fetchStudies, isInternalUser, sponsorContext]);

  const handleSponsorSelect = useCallback(() => {
    setShowSponsorSelector(false);
    fetchStudies();
  }, [fetchStudies]);

  // Force re-render when user changes
  useEffect(() => {
    setForceUpdate(prev => prev + 1);
  }, [user]);

  // Get permissions from AuthContext instead of making API calls
  const { userPermissions } = useContext(AuthContext);
  
  useEffect(() => {
    // Use permissions from AuthContext to avoid duplicate API calls
    setPermissions({
      canAccessStudyBuilder: userPermissions?.can_access_study_builder || false
    });
  }, [userPermissions]);

  // Save preferences whenever they change
  useEffect(() => {
    localStorage.setItem('studyViewPreferences.viewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('studyViewPreferences.sortBy', sortBy);
  }, [sortBy]);

  useEffect(() => {
    localStorage.setItem('studyViewPreferences.sortOrder', sortOrder);
  }, [sortOrder]);

  useEffect(() => {
    localStorage.setItem('studyViewPreferences.filterStatus', filterStatus);
  }, [filterStatus]);

  const filteredAndSortedStudies = useMemo(() => {
    if (!Array.isArray(studies)) return [];
    
    const titleFiltered = studies.filter(study => 
      study?.title?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    const statusFiltered = titleFiltered.filter(study => 
      (filterStatus === 'all' || study?.status === filterStatus)
    );
    
    const filtered = statusFiltered.sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'lastUpdated') {
        comparison = new Date(b?.updated_at || 0) - new Date(a?.updated_at || 0);
      } else if (sortBy === 'title') {
        comparison = (a?.title || '').localeCompare(b?.title || '');
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
      
    return filtered;
  }, [studies, searchTerm, sortBy, sortOrder, filterStatus]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.2, ease: 'easeOut' }
    }
  };

  const itemVariants = {
    hidden: { y: 8, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.25, ease: 'easeOut' }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'completed':
        return <CheckCircle className="h-3.5 w-3.5" />;
      case 'in_progress':
        return <Clock className="h-3.5 w-3.5" />;
      default:
        return <AlertCircle className="h-3.5 w-3.5" />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed':
        return 'badge-success';
      case 'in_progress':
        return 'badge-info';
      default:
        return 'badge-warning';
    }
  };

  const StudyCard = ({ study }) => (
    <motion.div
      variants={itemVariants}
      className="card group relative flex h-full cursor-pointer flex-col overflow-hidden transition-colors duration-200 hover:border-primary-300 dark:hover:border-primary-700"
      onClick={() => navigate(`/studies/${study.external_id}`)}
    >
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600 ring-1 ring-inset ring-primary-100 dark:bg-primary-900/30 dark:text-primary-400 dark:ring-primary-800/40">
            <Book className="h-5 w-5" />
          </div>
          {isInternalUser && (
            <span className={`badge capitalize ${getStatusColor(study.status)}`}>
              {getStatusIcon(study.status)}
              <span>{study.status.replace('_', ' ')}</span>
            </span>
          )}
        </div>
        
        <h2 className="mb-2 text-lg font-semibold leading-snug text-gray-900 transition-colors duration-200 group-hover:text-primary-700 dark:text-gray-100 dark:group-hover:text-primary-400">
          {study.title}
        </h2>
        
        <div className="mt-auto flex items-center pt-4 text-xs text-gray-500 dark:text-gray-400">
          <Calendar className="mr-1.5 h-3.5 w-3.5" />
          <span>Last updated: {formatDate(study.updated_at)}</span>
        </div>
      </div>
      
      <div className="mt-auto flex items-center justify-between border-t border-gray-100 bg-gray-50/60 px-5 py-3 dark:border-gray-700 dark:bg-gray-900/40">
        <span className="text-sm font-medium text-primary-700 dark:text-primary-400">View details</span>
        <ArrowUpRight className="h-4 w-4 text-primary-600 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 dark:text-primary-400" />
      </div>
    </motion.div>
  );

  const StudyListItem = ({ study }) => (
    <motion.div
      variants={itemVariants}
      className="card group cursor-pointer overflow-hidden transition-colors duration-200 hover:border-primary-300 dark:hover:border-primary-700"
      onClick={() => navigate(`/studies/${study.external_id}`)}
    >
      <div className="flex items-center justify-between p-4">
        <div className="flex min-w-0 flex-1 items-center">
          <div className="mr-4 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600 ring-1 ring-inset ring-primary-100 dark:bg-primary-900/30 dark:text-primary-400 dark:ring-primary-800/40">
            <Book className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-gray-900 transition-colors duration-200 group-hover:text-primary-700 dark:text-gray-100 dark:group-hover:text-primary-400">{study.title}</h2>
            <p className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <Calendar className="mr-1.5 h-3.5 w-3.5 flex-shrink-0" />
              <span>Last updated: {formatDate(study.updated_at)}</span>
            </p>
          </div>
        </div>
        <div className="ml-4 flex items-center gap-3">
          {isInternalUser && (
            <span className={`badge capitalize ${getStatusColor(study.status)}`}>
              {getStatusIcon(study.status)}
              <span>{study.status.replace('_', ' ')}</span>
            </span>
          )}
          <MoveRight className="h-4 w-4 text-primary-600 transition-transform duration-200 group-hover:translate-x-0.5 dark:text-primary-400" />
        </div>
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center" role="status">
        <Loader2 className="h-7 w-7 animate-spin text-primary-600 dark:text-primary-400" />
        <p className="mt-4 text-sm font-medium text-gray-600 dark:text-gray-300">Loading your studies...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="card w-full max-w-md">
          <EmptyState
            icon={AlertCircle}
            tone="danger"
            title="Unable to Load Studies"
            description={error}
            action={
              <button
                onClick={fetchStudies}
                className="btn btn-primary"
              >
                <RefreshCw className="h-4 w-4" />
                Try Again
              </button>
            }
          />
        </div>
      </div>
    );
  }

  if (showSponsorSelector) {
    return <SponsorSelector onSponsorSelect={handleSponsorSelect} />;
  }

  if (!loading && (!studies || studies.length === 0)) {
    return (
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="card py-6"
        >
          <EmptyState
            icon={Beaker}
            title="No Studies Found"
            description={isInternalUser
              ? "You haven't been assigned to any studies yet. Contact your administrator or create your first study to get started!"
              : "You haven't been assigned to any studies yet. Contact your study administrator for more information."}
            action={permissions.canAccessStudyBuilder ? (
              <button
                onClick={() => navigate('/studybuilder')}
                className="btn btn-primary"
              >
                <Plus className="h-4 w-4" />
                Create Your First Study
              </button>
            ) : null}
          />
        </motion.div>
      </div>
    );
  }

  return (
    <div>
      {/* Only internal users can reach the sponsor screen (FECOA-1784) */}
      {isInternalUser && (
        <button
          onClick={() => navigate('/sponsor-selector')}
          className="back-link"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Back to Sponsor</span>
        </button>
      )}
      <PageHeader
        icon={Book}
        title={sponsorName ? `${sponsorName} Study Dashboard` : 'Study Dashboard'}
        subtitle="Manage and explore your research studies"
      >

        {permissions.canAccessStudyBuilder && (
          <button
            onClick={() => navigate('/studybuilder')}
            className="btn btn-primary"
          >
            <ArrowRight className="h-4 w-4" />
            Study Builder
          </button>
        )}
      </PageHeader>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="card mb-6 p-4"
      >
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-grow">
            <input
              type="text"
              placeholder="Search studies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10"
            />
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
          </div>
          
          <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center lg:flex-none">
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="btn btn-secondary w-full justify-between sm:w-44"
              >
                <span className="flex items-center capitalize">
                  <Filter size={16} className="mr-2 text-gray-500 dark:text-gray-400" />
                  <span>{filterStatus === 'all' ? 'All Status' : filterStatus.replace('_', ' ')}</span>
                </span>
                <ChevronRight className={`h-4 w-4 text-gray-500 transition-transform duration-200 dark:text-gray-400 ${showDropdown ? 'rotate-90' : ''}`} />
              </button>
              
              {showDropdown && (
                <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-float dark:border-gray-700 dark:bg-gray-800">
                  <div className="py-1">
                    {['all', 'planned', 'in_progress', 'completed'].map((status) => (
                      <button
                        key={status}
                        className={`block w-full px-4 py-2 text-left text-sm capitalize transition-colors ${
                          filterStatus === status 
                            ? 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-400/10 dark:text-primary-300' 
                            : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-white/[0.06]'
                        }`}
                        onClick={() => {
                          setFilterStatus(status);
                          setShowDropdown(false);
                        }}
                      >
                        {status === 'all' ? 'All Status' : status.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-3">
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="input w-auto appearance-none pr-9"
                >
                  <option value="lastUpdated">Last Updated</option>
                  <option value="title">Title</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
              </div>
              
              <button
                onClick={() => setSortOrder(order => order === 'asc' ? 'desc' : 'asc')}
                className="btn btn-secondary btn-icon h-10 w-10"
                aria-label={sortOrder === 'asc' ? "Sort descending" : "Sort ascending"}
              >
                {sortOrder === 'asc' ? <SortAsc size={16} /> : <SortDesc size={16} />}
              </button>
              
              <div className="segmented">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`segmented-item h-9 px-2.5 ${viewMode === 'grid' ? 'segmented-item-active' : ''}`}
                  aria-label="Grid view"
                >
                  <Grid size={16} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`segmented-item h-9 px-2.5 ${viewMode === 'list' ? 'segmented-item-active' : ''}`}
                  aria-label="List view"
                >
                  <List size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <AnimatePresence mode="wait">
        {filteredAndSortedStudies.length === 0 ? (
          <motion.div
            key="empty-search"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="card"
          >
            <EmptyState
              icon={Search}
              title="No matching studies"
              description="No studies match your current search criteria. Try adjusting your filters or search terms."
              action={
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilterStatus('all');
                  }}
                  className="btn btn-secondary"
                >
                  Clear filters
                </button>
              }
            />
          </motion.div>
        ) : viewMode === 'grid' ? (
          <motion.div
            key="grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit={{ 
              opacity: 0,
              transition: { duration: 0.2 } 
            }}
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          >
            {filteredAndSortedStudies.map((study) => (
              <StudyCard key={study.id} study={study} />
            ))}
            
            {permissions.canAccessStudyBuilder && (
              <motion.div
                variants={itemVariants}
                className="group h-full cursor-pointer overflow-hidden rounded-xl bg-primary-600 shadow-card transition-colors duration-200 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600"
                onClick={() => navigate('/studybuilder/create')}
              >
                <div className="flex h-full flex-col p-5">
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 ring-1 ring-inset ring-white/20">
                    <Plus className="h-5 w-5 text-white" />
                  </div>
                  
                  <div className="mt-auto">
                    <h2 className="mb-1 text-lg font-semibold text-white">Create New Study</h2>
                    <p className="text-sm text-primary-50/90">Create a new study and configure all details</p>
                  </div>
                  
                  <div className="mt-5 flex items-center text-sm text-primary-50">
                    <span className="mr-2 font-medium">Get started</span>
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="list"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit={{ 
              opacity: 0,
              transition: { duration: 0.2 } 
            }}
            className="flex flex-col gap-3"
          >
            {filteredAndSortedStudies.map((study) => (
              <StudyListItem key={study.id} study={study} />
            ))}
            
            {permissions.canAccessStudyBuilder && (
              <motion.div
                variants={itemVariants}
                className="group cursor-pointer overflow-hidden rounded-xl bg-primary-600 shadow-card transition-colors duration-200 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600"
                onClick={() => navigate('/studybuilder/create')}
              >
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center">
                    <div className="mr-4 flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 ring-1 ring-inset ring-white/20">
                      <Plus className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-white">Create New Study</h2>
                      <p className="text-sm text-primary-50/90">Create a new study and configure all details</p>
                    </div>
                  </div>
                  <MoveRight className="h-4 w-4 text-white transition-transform duration-200 group-hover:translate-x-0.5" />
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StudiesPage;